import type { FindingSummary } from '../../lib/api'

/*
 * The findings list's narrowing filters: area, project, owner and standard (codex review,
 * 2026-09-27: sixty-five findings on one Recommendations page, with nothing to narrow them by but
 * severity and a search box).
 *
 * They filter WHAT HAS BEEN LOADED, like the search box and unlike the severity pills. The list loads
 * every page its server query matches (up to a cap it states), so over a whole list they are exact;
 * past the cap they look only at the rows loaded, and the page says so. The server is not asked:
 * three of the four are fields it does not filter on, and a filter that asked the server for one and
 * filtered the rest in the browser would behave two ways behind one row of controls.
 *
 * They live in the address (?area=Email&owner=none), beside the ?q= search the header hands over, so
 * a narrowed list survives opening a finding and coming back, and can be sent to a colleague.
 */

/** The ungrouped bucket's name: a finding with no workstream in the catalogue. */
export const OTHER_PROJECT = 'Other'

/** Owner filter values that are not an email. Neither contains "@", so no email can collide. */
export const OWNER_NONE = 'none'
export const OWNER_ANY = 'any'

export type Standard = 'cisa' | 'defenssive'

export interface ListFilters {
  area: string | null
  project: string | null
  /** OWNER_NONE, OWNER_ANY, or a member's email. */
  owner: string | null
  standard: Standard | null
}

export type FilterKey = keyof ListFilters

/** The address keys, one per filter, named as the page labels them. */
export const FILTER_KEYS: readonly FilterKey[] = ['area', 'project', 'owner', 'standard']

export const NO_FILTERS: ListFilters = { area: null, project: null, owner: null, standard: null }

/**
 * The filters an address asks for. An area page is one area already, so it ignores ?area=; an
 * unknown standard is dropped rather than kept, since no option could show it as chosen.
 */
export function readFilters(params: URLSearchParams, fixedArea?: string): ListFilters {
  const text = (key: string) => {
    const value = params.get(key)?.trim()
    return value ? value : null
  }
  const standard = params.get('standard')
  return {
    area: fixedArea === undefined ? text('area') : null,
    project: text('project'),
    owner: text('owner'),
    standard: standard === 'cisa' || standard === 'defenssive' ? standard : null,
  }
}

export function anyFilter(filters: ListFilters): boolean {
  return FILTER_KEYS.some((key) => filters[key] !== null)
}

/** The project a finding belongs to: its workstream, or the ungrouped bucket. */
export function projectOf(finding: FindingSummary): string {
  return finding.workstream ?? OTHER_PROJECT
}

/**
 * Which standard a finding is raised under, or null when the server did not say.
 *
 * FAILS CLOSED, as the finding page's Standard line does: a CISA policy id is CISA SCuBA, `null` is
 * one of Defenssive's own checks, and a field that is absent (an older server) or blank is NEITHER
 * -- filed under no standard rather than guessed into one, so a CISA finding is never disowned and a
 * Defenssive check never borrows CISA's name.
 */
export function standardOf(finding: FindingSummary): Standard | null {
  const reference = finding.scubaReference
  if (reference === null) return 'defenssive'
  if (typeof reference === 'string' && reference.trim() !== '') return 'cisa'
  return null
}

/**
 * Whether a finding's owner is what the Owner filter asks for. A row whose owner the server did not
 * send (absent: an older server) is neither assigned nor unassigned, so it matches no owner value.
 */
function ownerMatches(owner: string | null | undefined, wanted: string): boolean {
  if (owner === undefined) return false
  if (wanted === OWNER_NONE) return owner === null
  if (wanted === OWNER_ANY) return owner !== null
  return owner === wanted
}

/** Whether a finding passes every filter, or every filter but `skip` (for a facet's own counts). */
export function passes(finding: FindingSummary, filters: ListFilters, skip?: FilterKey): boolean {
  if (skip !== 'area' && filters.area !== null && finding.area !== filters.area) return false
  if (skip !== 'project' && filters.project !== null && projectOf(finding) !== filters.project) return false
  if (skip !== 'owner' && filters.owner !== null && !ownerMatches(finding.owner, filters.owner)) return false
  if (skip !== 'standard' && filters.standard !== null && standardOf(finding) !== filters.standard) return false
  return true
}

export interface FacetOption {
  value: string
  label: string
  /** Findings this option would show, with the search and every OTHER filter applied. */
  count: number
}

/**
 * One filter's choices, each with the number of findings it would show.
 *
 * The counts are what choosing the option would show with the search and the other filters as they
 * are -- not a count over the whole list, which would promise "Email (12)" and then show two because
 * an owner was chosen. The choices themselves come from every loaded row, so they do not come and
 * go as other filters change; the chosen value is always among them, at 0 when nothing has it (an
 * address from a colleague whose list was different), so the select can show what is applied.
 */
export function facetOptions(
  items: FindingSummary[],
  filters: ListFilters,
  key: FilterKey,
  search: ((finding: FindingSummary) => boolean) | null,
): { all: number; options: FacetOption[] } {
  const base = items.filter((f) => (search ? search(f) : true) && passes(f, filters, key))
  const countWhere = (test: (f: FindingSummary) => boolean) => base.filter(test).length

  let options: FacetOption[] = []
  switch (key) {
    case 'area': {
      const values = withChosen(distinctSorted(items.map((f) => f.area)), filters.area)
      options = values.map((value) => ({ value, label: value, count: countWhere((f) => f.area === value) }))
      break
    }
    case 'project': {
      const names = distinctSorted(items.map(projectOf).filter((name) => name !== OTHER_PROJECT))
      const values = withChosen(
        items.some((f) => projectOf(f) === OTHER_PROJECT) ? [...names, OTHER_PROJECT] : names,
        filters.project,
      )
      options = values.map((value) => ({ value, label: value, count: countWhere((f) => projectOf(f) === value) }))
      break
    }
    case 'owner': {
      const emails = withChosen(
        distinctSorted(items.flatMap((f) => (typeof f.owner === 'string' ? [f.owner] : []))),
        filters.owner === OWNER_NONE || filters.owner === OWNER_ANY ? null : filters.owner,
      )
      options = [
        { value: OWNER_NONE, label: 'Not assigned', count: countWhere((f) => ownerMatches(f.owner, OWNER_NONE)) },
        { value: OWNER_ANY, label: 'Assigned to someone', count: countWhere((f) => ownerMatches(f.owner, OWNER_ANY)) },
        ...emails.map((value) => ({ value, label: value, count: countWhere((f) => f.owner === value) })),
      ]
      break
    }
    case 'standard': {
      options = [
        { value: 'cisa', label: 'CISA SCuBA', count: countWhere((f) => standardOf(f) === 'cisa') },
        { value: 'defenssive', label: "Defenssive's own checks", count: countWhere((f) => standardOf(f) === 'defenssive') },
      ]
      break
    }
  }

  return { all: base.length, options }
}

function distinctSorted(values: string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b))
}

function withChosen(values: string[], chosen: string | null): string[] {
  return chosen === null || values.includes(chosen) ? values : [...values, chosen]
}

/** Worst first. Unknown severities sink. */
const SEVERITY_RANK: Record<string, number> = { Critical: 0, High: 1, Medium: 2, Low: 3, Informational: 4 }

/** Cheapest first, as the impact × effort order has it. Unknown efforts sink. */
const EFFORT_RANK: Record<string, number> = { Low: 0, Medium: 1, High: 2 }

/**
 * A project's findings with its most important one first: the highest severity, then the impact ×
 * effort order (cheapest first within a severity), then the order the list already had -- which,
 * in the grouped views, is the server's impact order. Everything else keeps its place.
 */
export function leadFirst(findings: FindingSummary[]): FindingSummary[] {
  if (findings.length < 2) return findings
  const rank = (f: FindingSummary) => [SEVERITY_RANK[f.severity] ?? 9, EFFORT_RANK[f.effort] ?? 9] as const

  let lead = 0
  for (let i = 1; i < findings.length; i += 1) {
    const [severity, effort] = rank(findings[i]!)
    const [bestSeverity, bestEffort] = rank(findings[lead]!)
    if (severity < bestSeverity || (severity === bestSeverity && effort < bestEffort)) lead = i
  }

  return lead === 0 ? findings : [findings[lead]!, ...findings.filter((_, i) => i !== lead)]
}
